import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaLink, FaUnlink, FaSync, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaSearch } from 'react-icons/fa';
import { SiQuickbooks } from 'react-icons/si';
import styles from './QuickBooksAccountModal.module.css';
import { useAccessToken, useLockBodyScroll } from '../../hooks';
import { useQuickBooks } from '../../hooks/useQuickBooks';
import { useAlert } from '../../context/AlertContext';
import ConfirmDialog from './ConfirmDialog';

/**
 * QuickBooksAccountModal Component
 * Manages QuickBooks customer linking for LIMS customers
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {number} props.clientId - LIMS client ID
 * @param {string} props.clientName - LIMS client name
 * @param {string} props.clientCode - LIMS client code
 */
const QuickBooksAccountModal = ({ isOpen, onClose, clientId, clientName, clientCode, quickBooksCustomerId, onClientUpdated }) => {
    useLockBodyScroll(isOpen);
    
    const { getBackendAccessToken } = useAccessToken();
    const { status, loading: qbLoading, error: qbError, isConnected, connect, refetch } = useQuickBooks();
    const { setErrorAlert, setInfoAlert } = useAlert();
    
    const [loading, setLoading] = useState(false);
    const [qboCustomerId, setQboCustomerId] = useState('');
    const [qboCustomerName, setQboCustomerName] = useState('');
    const [isLinked, setIsLinked] = useState(false);
    const [currentQuickBooksCustomerId, setCurrentQuickBooksCustomerId] = useState(quickBooksCustomerId || null);
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [searchMessage, setSearchMessage] = useState(null);
    const [customerData, setCustomerData] = useState(null);
    const [loadingCustomerData, setLoadingCustomerData] = useState(false);
    const [customerDataError, setCustomerDataError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        displayName: '',
        givenName: '',
        familyName: '',
        companyName: '',
        primaryEmailAddr: '',
        primaryPhone: '',
        mobile: '',
        billAddrLine1: '',
        billAddrLine2: '',
        billAddrCity: '',
        billAddrCountrySubDivisionCode: '',
        billAddrPostalCode: '',
        billAddrCountry: 'USA',
        notes: ''
    });
    const [creatingCustomer, setCreatingCustomer] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [editFormData, setEditFormData] = useState(null);
    const [updatingCustomer, setUpdatingCustomer] = useState(false);
    const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
    
    // Check if client is linked to QBO using the QuickBooksCustomerId field
    const checkQboLink = useCallback(async () => {
        try {
            setLoading(true);
            
            // Check if QuickBooksCustomerId exists on the client
            const isCurrentlyLinked = !!currentQuickBooksCustomerId;
            setIsLinked(isCurrentlyLinked);
            
            if (isCurrentlyLinked) {
                setQboCustomerId(currentQuickBooksCustomerId);
                setQboCustomerName(currentQuickBooksCustomerId);
                
                // Fetch customer data from QuickBooks
                await fetchCustomerData(currentQuickBooksCustomerId);
            } else {
                // Clear customer data if not linked
                setCustomerData(null);
                setCustomerDataError(null);
            }
        } catch (error) {
            console.error('Failed to check QBO link:', error);
            setErrorAlert('Failed to check QuickBooks link status');
        } finally {
            setLoading(false);
        }
    }, [currentQuickBooksCustomerId, setErrorAlert]);
    
    // Fetch customer data from QuickBooks API
    const fetchCustomerData = useCallback(async (customerId) => {
        try {
            setLoadingCustomerData(true);
            setCustomerDataError(null);
            const accessToken = await getBackendAccessToken();
            
            // Import quickbooksService dynamically
            const { default: quickbooksService } = await import('../../services/quickbooksService');
            
            // Call QuickBooks API to get customer by ID
            const response = await quickbooksService.getCustomerById(accessToken, customerId);
            
            // Backend returns {customer: object, message: string}
            const customer = response?.customer || null;
            setCustomerData(customer);
            
            if (customer) {
                setQboCustomerName(customer.displayName || customerId);
            }
        } catch (error) {
            console.error('Failed to fetch QuickBooks customer data:', error);
            setCustomerDataError(error.message || 'Failed to load customer data from QuickBooks');
            
            // Still set a basic name so UI doesn't look broken
            setQboCustomerName(customerId);
        } finally {
            setLoadingCustomerData(false);
        }
    }, [getBackendAccessToken]);
    
    // Sync with props when quickBooksCustomerId changes
    useEffect(() => {
        setCurrentQuickBooksCustomerId(quickBooksCustomerId || null);
    }, [quickBooksCustomerId]);
    
    // Load QBO link status when modal opens
    useEffect(() => {
        if (isOpen && isConnected) {
            checkQboLink();
        }
    }, [isOpen, isConnected, checkQboLink]);
    
    // Handle linking to QBO customer
    const handleLink = async () => {
        if (!qboCustomerId.trim()) {
            setErrorAlert('Please enter a QuickBooks Customer ID');
            return;
        }
        
        await handleLinkWithId(qboCustomerId.trim());
    };
    
    // Handle unlinking from QBO customer
    const handleUnlink = async () => {
        setShowUnlinkConfirm(true);
    };
    
    // Confirm and execute unlink
    const confirmUnlink = async () => {
        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            
            // Import clientService dynamically to avoid circular dependencies
            const { clientService } = await import('../../services/clientService');
            
            // Use dedicated QuickBooks unlink endpoint
            await clientService.unlinkFromQuickBooks(accessToken, clientId);
            
            // Notify parent to refresh data FIRST
            if (onClientUpdated) {
                await onClientUpdated();
            }
            
            // Update local state
            setCurrentQuickBooksCustomerId(null);
            setIsLinked(false);
            setQboCustomerId('');
            setQboCustomerName('');
            
            setInfoAlert('Successfully unlinked from QuickBooks customer');
        } catch (error) {
            console.error('Failed to unlink from QBO:', error);
            const errorMsg = error.message || 'Failed to unlink from QuickBooks customer';
            setErrorAlert(errorMsg);
        } finally {
            setLoading(false);
            setShowUnlinkConfirm(false);
        }
    };
    
    // Handle connecting to QuickBooks if not connected
    const handleConnectToQuickBooks = async () => {
        try {
            await connect();
        } catch (error) {
            console.error('Failed to connect to QuickBooks:', error);
            setErrorAlert('Failed to connect to QuickBooks');
        }
    };
    
    // Handle searching for QBO customer by name
    const handleSearchByName = async () => {
        if (!clientName || !clientName.trim()) {
            setErrorAlert('Client name is required to search QuickBooks');
            return;
        }
        
        try {
            setSearching(true);
            setSearchResults([]);
            setSearchMessage(null);
            const accessToken = await getBackendAccessToken();
            
            // Import quickbooksService dynamically
            const { default: quickbooksService } = await import('../../services/quickbooksService');
            
            // Call QuickBooks API to search for customers by name
            const results = await quickbooksService.searchCustomers(accessToken, clientName);
            
            // Backend returns {count: number, customers: array}
            const customerList = results?.customers || [];
            setSearchResults(customerList);
            
            if (customerList.length === 0) {
                setSearchMessage({ type: 'info', text: `No QuickBooks customers found matching "${clientName}". You can try manual entry instead.` });
                setShowManualEntry(true);
            } else {
                setSearchMessage({ type: 'success', text: `Found ${customerList.length} matching customer${customerList.length === 1 ? '' : 's'}` });
            }
        } catch (error) {
            console.error('Failed to search QuickBooks customers:', error);
            
            // Check for authorization error
            const errorMessage = error.message || '';
            if (errorMessage.includes('ApplicationAuthorizationFailed') || errorMessage.includes('403')) {
                setSearchMessage({ type: 'error', text: 'QuickBooks authorization error. Please disconnect and reconnect to QuickBooks to grant proper permissions.' });
            } else {
                setSearchMessage({ type: 'error', text: `Failed to search QuickBooks customers: ${errorMessage}` });
            }
            
            setShowManualEntry(true);
        } finally {
            setSearching(false);
        }
    };
    
    // Handle selecting a customer from search results
    const handleSelectSearchResult = async (customer) => {
        setQboCustomerId(customer.id);
        await handleLinkWithId(customer.id);
    };
    
    // Handle linking with a specific ID (extracted from handleLink for reuse)
    const handleLinkWithId = async (customerId) => {
        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            
            // Import clientService dynamically to avoid circular dependencies
            const { clientService } = await import('../../services/clientService');
            
            // Use dedicated QuickBooks link endpoint
            await clientService.linkToQuickBooks(accessToken, clientId, customerId);
            
            // Notify parent to refresh data FIRST
            if (onClientUpdated) {
                await onClientUpdated();
            }
            
            // Update local state
            setCurrentQuickBooksCustomerId(customerId);
            setIsLinked(true);
            setQboCustomerName(customerId);
            
            // Clear search results and inputs
            setSearchResults([]);
            setQboCustomerId('');
            setShowManualEntry(false);
            
            setInfoAlert('Successfully linked to QuickBooks customer');
        } catch (error) {
            console.error('Failed to link to QBO:', error);
            const errorMsg = error.message || 'Failed to link to QuickBooks customer';
            setErrorAlert(errorMsg);
        } finally {
            setLoading(false);
        }
    };
    
    // Handle refreshing customer data from QuickBooks
    const handleRefreshCustomerData = async () => {
        if (currentQuickBooksCustomerId) {
            await fetchCustomerData(currentQuickBooksCustomerId);
        }
    };
    
    // Handle creating a new QBO customer and linking it
    const handleCreateCustomer = async () => {
        // Validate required field
        if (!createFormData.displayName.trim()) {
            setErrorAlert('Display Name is required to create a QuickBooks customer');
            return;
        }
        
        try {
            setCreatingCustomer(true);
            const accessToken = await getBackendAccessToken();
            
            // Import quickbooksService dynamically
            const { default: quickbooksService } = await import('../../services/quickbooksService');
            
            // Create customer in QuickBooks
            const response = await quickbooksService.createCustomer(accessToken, createFormData);
            
            // Backend returns {customer: object, customerId: string, message: string}
            const newCustomerId = response?.customerId || response?.customer?.id;
            
            if (!newCustomerId) {
                throw new Error('No customer ID returned from QuickBooks');
            }
            
            setInfoAlert(`Successfully created QuickBooks customer: ${response.customer.displayName}`);
            
            // Now link this new customer to the LIMS client
            await handleLinkWithId(newCustomerId);
            
            // Clear form and close create mode
            setCreateFormData({
                displayName: '',
                givenName: '',
                familyName: '',
                companyName: '',
                primaryEmailAddr: '',
                primaryPhone: '',
                mobile: '',
                billAddrLine1: '',
                billAddrLine2: '',
                billAddrCity: '',
                billAddrCountrySubDivisionCode: '',
                billAddrPostalCode: '',
                billAddrCountry: 'USA',
                notes: ''
            });
            setShowCreateForm(false);
            setSearchResults([]);
        } catch (error) {
            console.error('Failed to create QuickBooks customer:', error);
            const errorMsg = error.message || 'Failed to create QuickBooks customer';
            setErrorAlert(errorMsg);
        } finally {
            setCreatingCustomer(false);
        }
    };
    
    // Handle updating create form data
    const handleCreateFormChange = (field, value) => {
        setCreateFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    // Pre-fill create form with client data
    const handleShowCreateForm = () => {
        // Pre-fill with client name if available
        if (clientName && !createFormData.displayName) {
            setCreateFormData(prev => ({
                ...prev,
                displayName: clientName,
                companyName: clientName,
                notes: `Created from LIMS client: ${clientName}${clientCode ? ` (${clientCode})` : ''}`
            }));
        }
        setShowCreateForm(true);
        setShowManualEntry(false);
        setSearchResults([]);
    };
    
    // Handle showing edit form and pre-filling with current customer data
    const handleShowEditForm = () => {
        if (!customerData) {
            setErrorAlert('Customer data not loaded. Please refresh and try again.');
            return;
        }
        
        // Pre-fill edit form with current customer data
        setEditFormData({
            id: customerData.id,
            syncToken: customerData.syncToken,
            displayName: customerData.displayName || '',
            givenName: customerData.givenName || '',
            middleName: customerData.middleName || '',
            familyName: customerData.familyName || '',
            companyName: customerData.companyName || '',
            primaryEmailAddr: customerData.primaryEmailAddr || '',
            primaryPhone: customerData.primaryPhone || '',
            mobile: customerData.mobile || '',
            billAddrLine1: customerData.billAddrLine1 || '',
            billAddrLine2: customerData.billAddrLine2 || '',
            billAddrCity: customerData.billAddrCity || '',
            billAddrCountrySubDivisionCode: customerData.billAddrCountrySubDivisionCode || '',
            billAddrPostalCode: customerData.billAddrPostalCode || '',
            billAddrCountry: customerData.billAddrCountry || 'USA',
            notes: customerData.notes || ''
        });
        setShowEditForm(true);
    };
    
    // Handle updating edit form data
    const handleEditFormChange = (field, value) => {
        setEditFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    // Handle updating an existing QBO customer
    const handleUpdateCustomer = async () => {
        // Validate required fields
        if (!editFormData.displayName.trim()) {
            setErrorAlert('Display Name is required to update a QuickBooks customer');
            return;
        }
        
        try {
            setUpdatingCustomer(true);
            const accessToken = await getBackendAccessToken();
            
            // Import quickbooksService dynamically
            const { default: quickbooksService } = await import('../../services/quickbooksService');
            
            // Update customer in QuickBooks
            const response = await quickbooksService.updateCustomer(
                accessToken,
                editFormData.id,
                editFormData
            );
            
            // Backend returns {customer: object, message: string}
            const updatedCustomer = response?.customer;
            
            if (!updatedCustomer) {
                throw new Error('No customer data returned from QuickBooks');
            }
            
            setInfoAlert(`Successfully updated QuickBooks customer: ${updatedCustomer.displayName}`);
            
            // Refresh customer data to show updated information
            await fetchCustomerData(editFormData.id);
            
            // Close edit form
            setShowEditForm(false);
            setEditFormData(null);
        } catch (error) {
            console.error('Failed to update QuickBooks customer:', error);
            const errorMsg = error.message || 'Failed to update QuickBooks customer';
            setErrorAlert(errorMsg);
        } finally {
            setUpdatingCustomer(false);
        }
    };
    
    if (!isOpen) return null;
    
    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerInfo}>
                        <div className={styles.headerIcon}>
                            <SiQuickbooks />
                        </div>
                        <div>
                            <h2 className={styles.modalTitle}>QuickBooks Account</h2>
                            <p className={styles.modalSubtitle}>
                                {clientName}{clientCode && ` (${clientCode})`}
                            </p>
                        </div>
                    </div>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        title="Close"
                    >
                        <FaTimes />
                    </button>
                </div>
                
                {/* Modal Body */}
                <div className={styles.modalBody}>
                    {qbLoading ? (
                        <div className={styles.loadingState}>
                            <p>Checking QuickBooks connection...</p>
                        </div>
                    ) : !isConnected ? (
                        <div className={styles.notConnected}>
                            <FaExclamationTriangle className={styles.warningIcon} />
                            <p>Prism is not currently connected to your company's QuickBooks</p>
                            {qbError && (
                                <div className={styles.errorMessage}>
                                    <FaTimesCircle /> {qbError}
                                </div>
                            )}
                            <button
                                className={styles.connectButton}
                                onClick={handleConnectToQuickBooks}
                                disabled={loading}
                            >
                                <FaLink /> Connect to QuickBooks
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Current Link Status */}
                            {isLinked ? (
                                <div className={styles.linkStatus}>
                                    <div className={styles.linkedInfo}>
                                        <FaCheckCircle className={styles.linkedIcon} />
                                        <div className={styles.linkedDetails}>
                                            <h4>Linked to QuickBooks Customer</h4>
                                            <p className={styles.qboCustomerId}>QuickBooks Customer ID: {currentQuickBooksCustomerId}</p>
                                            
                                            {loadingCustomerData ? (
                                                <div className={styles.customerDataLoading}>
                                                    <p>Loading customer data from QuickBooks...</p>
                                                </div>
                                            ) : customerDataError ? (
                                                <div className={styles.customerDataError}>
                                                    <FaTimesCircle /> {customerDataError}
                                                    <button
                                                        className={styles.retryButton}
                                                        onClick={handleRefreshCustomerData}
                                                        disabled={loadingCustomerData}
                                                    >
                                                        <FaSync /> Retry
                                                    </button>
                                                </div>
                                            ) : customerData ? (
                                                <div className={styles.customerDataSection}>
                                                    <div className={styles.customerDataGrid}>
                                                        <div className={styles.customerDataRow}>
                                                            <strong>Display Name:</strong>
                                                            <span>{customerData.displayName}</span>
                                                        </div>
                                                        
                                                        {customerData.companyName && (
                                                            <div className={styles.customerDataRow}>
                                                                <strong>Company:</strong>
                                                                <span>{customerData.companyName}</span>
                                                            </div>
                                                        )}
                                                        
                                                        {(customerData.givenName || customerData.familyName) && (
                                                            <div className={styles.customerDataRow}>
                                                                <strong>Name:</strong>
                                                                <span>
                                                                    {[customerData.givenName, customerData.middleName, customerData.familyName]
                                                                        .filter(Boolean)
                                                                        .join(' ')}
                                                                </span>
                                                            </div>
                                                        )}
                                                        
                                                        {customerData.primaryEmailAddr && (
                                                            <div className={styles.customerDataRow}>
                                                                <strong>Email:</strong>
                                                                <span>{customerData.primaryEmailAddr}</span>
                                                            </div>
                                                        )}
                                                        
                                                        {customerData.primaryPhone && (
                                                            <div className={styles.customerDataRow}>
                                                                <strong>Phone:</strong>
                                                                <span>{customerData.primaryPhone}</span>
                                                            </div>
                                                        )}
                                                        
                                                        {customerData.mobile && (
                                                            <div className={styles.customerDataRow}>
                                                                <strong>Mobile:</strong>
                                                                <span>{customerData.mobile}</span>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Billing Address */}
                                                        {(customerData.billAddrLine1 || customerData.billAddrCity) && (
                                                            <div className={styles.customerDataRow}>
                                                                <strong>Billing Address:</strong>
                                                                <span>
                                                                    {[
                                                                        customerData.billAddrLine1,
                                                                        customerData.billAddrLine2,
                                                                        customerData.billAddrLine3,
                                                                        [
                                                                            customerData.billAddrCity,
                                                                            customerData.billAddrCountrySubDivisionCode
                                                                        ].filter(Boolean).join(', '),
                                                                        customerData.billAddrPostalCode,
                                                                        customerData.billAddrCountry
                                                                    ].filter(Boolean).join(', ')}
                                                                </span>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Shipping Address */}
                                                        {(customerData.shipAddrLine1 || customerData.shipAddrCity) && (
                                                            <div className={styles.customerDataRow}>
                                                                <strong>Shipping Address:</strong>
                                                                <span>
                                                                    {[
                                                                        customerData.shipAddrLine1,
                                                                        customerData.shipAddrLine2,
                                                                        customerData.shipAddrLine3,
                                                                        [
                                                                            customerData.shipAddrCity,
                                                                            customerData.shipAddrCountrySubDivisionCode
                                                                        ].filter(Boolean).join(', '),
                                                                        customerData.shipAddrPostalCode,
                                                                        customerData.shipAddrCountry
                                                                    ].filter(Boolean).join(', ')}
                                                                </span>
                                                            </div>
                                                        )}
                                                        
                                                        {customerData.balance !== undefined && customerData.balance !== null && (
                                                            <div className={styles.customerDataRow}>
                                                                <strong>Balance:</strong>
                                                                <span className={styles.balanceAmount}>
                                                                    ${customerData.balance.toFixed(2)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        
                                                        <div className={styles.customerDataRow}>
                                                            <strong>Status:</strong>
                                                            <span className={customerData.active ? styles.statusActive : styles.statusInactive}>
                                                                {customerData.active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </div>
                                                        
                                                        {customerData.metaDataLastUpdatedTime && (
                                                            <div className={styles.customerDataRow}>
                                                                <strong>Last Updated:</strong>
                                                                <span>{new Date(customerData.metaDataLastUpdatedTime).toLocaleString()}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    <button
                                                        className={styles.refreshDataButton}
                                                        onClick={handleRefreshCustomerData}
                                                        disabled={loadingCustomerData}
                                                        title="Refresh customer data from QuickBooks"
                                                    >
                                                        <FaSync /> {loadingCustomerData ? 'Refreshing...' : 'Refresh Data'}
                                                    </button>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className={styles.linkedActions}>
                                        <button
                                            className={styles.editButton}
                                            onClick={handleShowEditForm}
                                            disabled={loading || loadingCustomerData || !customerData || showEditForm}
                                        >
                                            <FaSync /> {showEditForm ? 'Editing...' : 'Edit Customer'}
                                        </button>
                                        <button
                                            className={styles.unlinkButton}
                                            onClick={handleUnlink}
                                            disabled={loading || showEditForm}
                                        >
                                            <FaUnlink /> Unlink Customer
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.linkForm}>
                                    <h4>Link to QuickBooks Customer</h4>
                                    <p className={styles.helpText}>
                                        Search for a QuickBooks customer by name or enter the Customer ID manually.
                                    </p>
                                    
                                    {/* Search by Name Button */}
                                    <div className={styles.searchSection}>
                                        <button
                                            className={styles.searchButton}
                                            onClick={handleSearchByName}
                                            disabled={loading || searching || !clientName || showCreateForm}
                                        >
                                            <FaSearch /> {searching ? 'Searching...' : 'Find by Name'}
                                        </button>
                                        <button
                                            className={styles.manualEntryButton}
                                            onClick={() => {
                                                setShowManualEntry(!showManualEntry);
                                                setShowCreateForm(false);
                                            }}
                                            disabled={loading || searching || showCreateForm}
                                        >
                                            {showManualEntry ? 'Hide Manual Entry' : 'Enter Manually'}
                                        </button>
                                        <button
                                            className={styles.createButton}
                                            onClick={handleShowCreateForm}
                                            disabled={loading || searching || creatingCustomer || showManualEntry}
                                        >
                                            {showCreateForm ? 'Hide Create Form' : 'Create QBO Customer'}
                                        </button>
                                    </div>
                                    
                                    {/* Search Message */}
                                    {searchMessage && (
                                        <div className={`${styles.searchMessage} ${styles[searchMessage.type]}`}>
                                            {searchMessage.type === 'error' && <FaTimesCircle />}
                                            {searchMessage.type === 'success' && <FaCheckCircle />}
                                            {searchMessage.type === 'info' && <FaExclamationTriangle />}
                                            <span>{searchMessage.text}</span>
                                        </div>
                                    )}
                                    
                                    {/* Search Results */}
                                    {searchResults.length > 0 && (
                                        <div className={styles.searchResults}>
                                            <h5>Select a QuickBooks Customer:</h5>
                                            {searchResults.map((customer) => (
                                                <div
                                                    key={customer.id}
                                                    className={styles.searchResultItem}
                                                    onClick={() => handleSelectSearchResult(customer)}
                                                >
                                                    <div className={styles.resultInfo}>
                                                        <strong>{customer.displayName}</strong>
                                                        <span className={styles.resultId}>ID: {customer.id}</span>
                                                    </div>
                                                    <FaLink className={styles.resultIcon} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Manual Entry */}
                                    {showManualEntry && (
                                        <div className={styles.manualEntry}>
                                            <div className={styles.inputGroup}>
                                                <label htmlFor="qboCustomerId">QuickBooks Customer ID</label>
                                                <input
                                                    id="qboCustomerId"
                                                    type="text"
                                                    className={styles.input}
                                                    value={qboCustomerId}
                                                    onChange={(e) => setQboCustomerId(e.target.value)}
                                                    placeholder="Enter Customer ID"
                                                    disabled={loading}
                                                />
                                            </div>
                                            <button
                                                className={styles.linkButton}
                                                onClick={handleLink}
                                                disabled={loading || !qboCustomerId.trim()}
                                            >
                                                <FaLink /> Link Customer
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* Create Customer Form */}
                                    {showCreateForm && (
                                        <div className={styles.createCustomerForm}>
                                            <h5>Create New QuickBooks Customer</h5>
                                            <p className={styles.formHelpText}>
                                                Fill in the details below to create a new customer in QuickBooks. 
                                                The customer will be automatically linked to this LIMS client.
                                            </p>
                                            
                                            <div className={styles.formGrid}>
                                                {/* Display Name - Required */}
                                                <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                                                    <label htmlFor="displayName">
                                                        Display Name <span className={styles.required}>*</span>
                                                    </label>
                                                    <input
                                                        id="displayName"
                                                        type="text"
                                                        className={styles.input}
                                                        value={createFormData.displayName}
                                                        onChange={(e) => handleCreateFormChange('displayName', e.target.value)}
                                                        placeholder="How this customer appears in QuickBooks"
                                                        disabled={creatingCustomer}
                                                        required
                                                    />
                                                </div>
                                                
                                                {/* Company Name */}
                                                <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                                                    <label htmlFor="companyName">Company Name</label>
                                                    <input
                                                        id="companyName"
                                                        type="text"
                                                        className={styles.input}
                                                        value={createFormData.companyName}
                                                        onChange={(e) => handleCreateFormChange('companyName', e.target.value)}
                                                        placeholder="Company name"
                                                        disabled={creatingCustomer}
                                                    />
                                                </div>
                                                
                                                {/* Name Fields */}
                                                <div className={styles.inputGroup}>
                                                    <label htmlFor="givenName">First Name</label>
                                                    <input
                                                        id="givenName"
                                                        type="text"
                                                        className={styles.input}
                                                        value={createFormData.givenName}
                                                        onChange={(e) => handleCreateFormChange('givenName', e.target.value)}
                                                        placeholder="First name"
                                                        disabled={creatingCustomer}
                                                    />
                                                </div>
                                                
                                                <div className={styles.inputGroup}>
                                                    <label htmlFor="familyName">Last Name</label>
                                                    <input
                                                        id="familyName"
                                                        type="text"
                                                        className={styles.input}
                                                        value={createFormData.familyName}
                                                        onChange={(e) => handleCreateFormChange('familyName', e.target.value)}
                                                        placeholder="Last name"
                                                        disabled={creatingCustomer}
                                                    />
                                                </div>
                                                
                                                {/* Contact Information */}
                                                <div className={styles.inputGroup}>
                                                    <label htmlFor="primaryEmailAddr">Email</label>
                                                    <input
                                                        id="primaryEmailAddr"
                                                        type="email"
                                                        className={styles.input}
                                                        value={createFormData.primaryEmailAddr}
                                                        onChange={(e) => handleCreateFormChange('primaryEmailAddr', e.target.value)}
                                                        placeholder="email@example.com"
                                                        disabled={creatingCustomer}
                                                    />
                                                </div>
                                                
                                                <div className={styles.inputGroup}>
                                                    <label htmlFor="primaryPhone">Phone</label>
                                                    <input
                                                        id="primaryPhone"
                                                        type="tel"
                                                        className={styles.input}
                                                        value={createFormData.primaryPhone}
                                                        onChange={(e) => handleCreateFormChange('primaryPhone', e.target.value)}
                                                        placeholder="555-1234"
                                                        disabled={creatingCustomer}
                                                    />
                                                </div>
                                                
                                                <div className={styles.inputGroup}>
                                                    <label htmlFor="mobile">Mobile</label>
                                                    <input
                                                        id="mobile"
                                                        type="tel"
                                                        className={styles.input}
                                                        value={createFormData.mobile}
                                                        onChange={(e) => handleCreateFormChange('mobile', e.target.value)}
                                                        placeholder="555-5678"
                                                        disabled={creatingCustomer}
                                                    />
                                                </div>
                                                
                                                {/* Billing Address */}
                                                <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                                                    <label htmlFor="billAddrLine1">Billing Address Line 1</label>
                                                    <input
                                                        id="billAddrLine1"
                                                        type="text"
                                                        className={styles.input}
                                                        value={createFormData.billAddrLine1}
                                                        onChange={(e) => handleCreateFormChange('billAddrLine1', e.target.value)}
                                                        placeholder="123 Main St"
                                                        disabled={creatingCustomer}
                                                    />
                                                </div>
                                                
                                                <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                                                    <label htmlFor="billAddrLine2">Billing Address Line 2</label>
                                                    <input
                                                        id="billAddrLine2"
                                                        type="text"
                                                        className={styles.input}
                                                        value={createFormData.billAddrLine2}
                                                        onChange={(e) => handleCreateFormChange('billAddrLine2', e.target.value)}
                                                        placeholder="Suite 100 (optional)"
                                                        disabled={creatingCustomer}
                                                    />
                                                </div>
                                                
                                                <div className={styles.inputGroup}>
                                                    <label htmlFor="billAddrCity">City</label>
                                                    <input
                                                        id="billAddrCity"
                                                        type="text"
                                                        className={styles.input}
                                                        value={createFormData.billAddrCity}
                                                        onChange={(e) => handleCreateFormChange('billAddrCity', e.target.value)}
                                                        placeholder="City"
                                                        disabled={creatingCustomer}
                                                    />
                                                </div>
                                                
                                                <div className={styles.inputGroup}>
                                                    <label htmlFor="billAddrCountrySubDivisionCode">State/Province</label>
                                                    <input
                                                        id="billAddrCountrySubDivisionCode"
                                                        type="text"
                                                        className={styles.input}
                                                        value={createFormData.billAddrCountrySubDivisionCode}
                                                        onChange={(e) => handleCreateFormChange('billAddrCountrySubDivisionCode', e.target.value)}
                                                        placeholder="CA"
                                                        disabled={creatingCustomer}
                                                        maxLength={2}
                                                    />
                                                </div>
                                                
                                                <div className={styles.inputGroup}>
                                                    <label htmlFor="billAddrPostalCode">Postal Code</label>
                                                    <input
                                                        id="billAddrPostalCode"
                                                        type="text"
                                                        className={styles.input}
                                                        value={createFormData.billAddrPostalCode}
                                                        onChange={(e) => handleCreateFormChange('billAddrPostalCode', e.target.value)}
                                                        placeholder="90001"
                                                        disabled={creatingCustomer}
                                                    />
                                                </div>
                                                
                                                <div className={styles.inputGroup}>
                                                    <label htmlFor="billAddrCountry">Country</label>
                                                    <input
                                                        id="billAddrCountry"
                                                        type="text"
                                                        className={styles.input}
                                                        value={createFormData.billAddrCountry}
                                                        onChange={(e) => handleCreateFormChange('billAddrCountry', e.target.value)}
                                                        placeholder="USA"
                                                        disabled={creatingCustomer}
                                                    />
                                                </div>
                                                
                                                {/* Notes */}
                                                <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                                                    <label htmlFor="notes">Notes</label>
                                                    <textarea
                                                        id="notes"
                                                        className={styles.textarea}
                                                        value={createFormData.notes}
                                                        onChange={(e) => handleCreateFormChange('notes', e.target.value)}
                                                        placeholder="Additional notes about this customer"
                                                        disabled={creatingCustomer}
                                                        rows={3}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className={styles.createFormActions}>
                                                <button
                                                    className={styles.cancelButton}
                                                    onClick={() => setShowCreateForm(false)}
                                                    disabled={creatingCustomer}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    className={styles.createSubmitButton}
                                                    onClick={handleCreateCustomer}
                                                    disabled={creatingCustomer || !createFormData.displayName.trim()}
                                                >
                                                    <FaLink /> {creatingCustomer ? 'Creating & Linking...' : 'Create & Link Customer'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Edit Customer Form */}
                            {showEditForm && editFormData && (
                                <div className={styles.editCustomerForm}>
                                    <h4>Edit QuickBooks Customer</h4>
                                    <p className={styles.formHelpText}>
                                        Update customer information in QuickBooks. Changes will be synced immediately.
                                    </p>
                                    
                                    <div className={styles.formGrid}>
                                        {/* Display Name - Required */}
                                        <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                                            <label htmlFor="editDisplayName">
                                                Display Name <span className={styles.required}>*</span>
                                            </label>
                                            <input
                                                id="editDisplayName"
                                                type="text"
                                                className={styles.input}
                                                value={editFormData.displayName}
                                                onChange={(e) => handleEditFormChange('displayName', e.target.value)}
                                                placeholder="How this customer appears in QuickBooks"
                                                disabled={updatingCustomer}
                                                required
                                            />
                                        </div>
                                        
                                        {/* Company Name */}
                                        <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                                            <label htmlFor="editCompanyName">Company Name</label>
                                            <input
                                                id="editCompanyName"
                                                type="text"
                                                className={styles.input}
                                                value={editFormData.companyName}
                                                onChange={(e) => handleEditFormChange('companyName', e.target.value)}
                                                placeholder="Company name"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        {/* Name Fields */}
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="editGivenName">First Name</label>
                                            <input
                                                id="editGivenName"
                                                type="text"
                                                className={styles.input}
                                                value={editFormData.givenName}
                                                onChange={(e) => handleEditFormChange('givenName', e.target.value)}
                                                placeholder="First name"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="editMiddleName">Middle Name</label>
                                            <input
                                                id="editMiddleName"
                                                type="text"
                                                className={styles.input}
                                                value={editFormData.middleName}
                                                onChange={(e) => handleEditFormChange('middleName', e.target.value)}
                                                placeholder="Middle name"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                                            <label htmlFor="editFamilyName">Last Name</label>
                                            <input
                                                id="editFamilyName"
                                                type="text"
                                                className={styles.input}
                                                value={editFormData.familyName}
                                                onChange={(e) => handleEditFormChange('familyName', e.target.value)}
                                                placeholder="Last name"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        {/* Contact Information */}
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="editPrimaryEmailAddr">Email</label>
                                            <input
                                                id="editPrimaryEmailAddr"
                                                type="email"
                                                className={styles.input}
                                                value={editFormData.primaryEmailAddr}
                                                onChange={(e) => handleEditFormChange('primaryEmailAddr', e.target.value)}
                                                placeholder="email@example.com"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="editPrimaryPhone">Phone</label>
                                            <input
                                                id="editPrimaryPhone"
                                                type="tel"
                                                className={styles.input}
                                                value={editFormData.primaryPhone}
                                                onChange={(e) => handleEditFormChange('primaryPhone', e.target.value)}
                                                placeholder="555-1234"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="editMobile">Mobile</label>
                                            <input
                                                id="editMobile"
                                                type="tel"
                                                className={styles.input}
                                                value={editFormData.mobile}
                                                onChange={(e) => handleEditFormChange('mobile', e.target.value)}
                                                placeholder="555-5678"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        {/* Billing Address */}
                                        <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                                            <label htmlFor="editBillAddrLine1">Billing Address Line 1</label>
                                            <input
                                                id="editBillAddrLine1"
                                                type="text"
                                                className={styles.input}
                                                value={editFormData.billAddrLine1}
                                                onChange={(e) => handleEditFormChange('billAddrLine1', e.target.value)}
                                                placeholder="123 Main St"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                                            <label htmlFor="editBillAddrLine2">Billing Address Line 2</label>
                                            <input
                                                id="editBillAddrLine2"
                                                type="text"
                                                className={styles.input}
                                                value={editFormData.billAddrLine2}
                                                onChange={(e) => handleEditFormChange('billAddrLine2', e.target.value)}
                                                placeholder="Suite 100 (optional)"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="editBillAddrCity">City</label>
                                            <input
                                                id="editBillAddrCity"
                                                type="text"
                                                className={styles.input}
                                                value={editFormData.billAddrCity}
                                                onChange={(e) => handleEditFormChange('billAddrCity', e.target.value)}
                                                placeholder="City"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="editBillAddrCountrySubDivisionCode">State/Province</label>
                                            <input
                                                id="editBillAddrCountrySubDivisionCode"
                                                type="text"
                                                className={styles.input}
                                                value={editFormData.billAddrCountrySubDivisionCode}
                                                onChange={(e) => handleEditFormChange('billAddrCountrySubDivisionCode', e.target.value)}
                                                placeholder="CA"
                                                disabled={updatingCustomer}
                                                maxLength={2}
                                            />
                                        </div>
                                        
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="editBillAddrPostalCode">Postal Code</label>
                                            <input
                                                id="editBillAddrPostalCode"
                                                type="text"
                                                className={styles.input}
                                                value={editFormData.billAddrPostalCode}
                                                onChange={(e) => handleEditFormChange('billAddrPostalCode', e.target.value)}
                                                placeholder="90001"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="editBillAddrCountry">Country</label>
                                            <input
                                                id="editBillAddrCountry"
                                                type="text"
                                                className={styles.input}
                                                value={editFormData.billAddrCountry}
                                                onChange={(e) => handleEditFormChange('billAddrCountry', e.target.value)}
                                                placeholder="USA"
                                                disabled={updatingCustomer}
                                            />
                                        </div>
                                        
                                        {/* Notes */}
                                        <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                                            <label htmlFor="editNotes">Notes</label>
                                            <textarea
                                                id="editNotes"
                                                className={styles.textarea}
                                                value={editFormData.notes}
                                                onChange={(e) => handleEditFormChange('notes', e.target.value)}
                                                placeholder="Additional notes about this customer"
                                                disabled={updatingCustomer}
                                                rows={3}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className={styles.editFormActions}>
                                        <button
                                            className={styles.cancelButton}
                                            onClick={() => {
                                                setShowEditForm(false);
                                                setEditFormData(null);
                                            }}
                                            disabled={updatingCustomer}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className={styles.updateButton}
                                            onClick={handleUpdateCustomer}
                                            disabled={updatingCustomer || !editFormData.displayName.trim()}
                                        >
                                            <FaSync /> {updatingCustomer ? 'Updating...' : 'Update Customer'}
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {/* Additional Actions */}
                            <div className={styles.additionalActions}>
                                <button
                                    className={styles.refreshButton}
                                    onClick={checkQboLink}
                                    disabled={loading}
                                    title="Refresh link status"
                                >
                                    <FaSync /> Refresh Status
                                </button>
                            </div>
                        </>
                    )}
                </div>
                
                {/* Modal Footer */}
                <div className={styles.modalFooter}>
                    <button
                        className={styles.closeButtonSecondary}
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
            
            {/* Unlink Confirmation Dialog */}
            <ConfirmDialog
                isOpen={showUnlinkConfirm}
                onClose={() => setShowUnlinkConfirm(false)}
                onConfirm={confirmUnlink}
                title="Unlink QuickBooks Customer"
                message="Are you sure you want to unlink this client from QuickBooks? This will remove the connection between the LIMS client and the QuickBooks customer."
                confirmText="Unlink"
                cancelText="Cancel"
                variant="warning"
                loading={loading}
            />
        </div>
    );
};

export default QuickBooksAccountModal;
